import { LucideIcon } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface StatCardProps {
  /** Icon to display */
  icon: LucideIcon
  /** Card label */
  label: string
  /** Numeric value to display */
  value: number | string
  /** Optional sublabel */
  sublabel?: string
  /** Visual variant */
  variant?: 'default' | 'success' | 'warning' | 'error'
  /** Loading state */
  isLoading?: boolean
  /** Click handler */
  onClick?: () => void
  /** Additional CSS classes */
  className?: string
}

const variantStyles = {
  default: 'text-primary bg-primary/10',
  success: 'text-green-600 bg-green-500/10',
  warning: 'text-orange-600 bg-orange-500/10',
  error: 'text-destructive bg-destructive/10',
}

/**
 * Metric display card with icon, count, and label.
 * 
 * @example
 * ```tsx
 * <StatCard
 *   icon={Activity}
 *   label="Active"
 *   value={3}
 *   variant="default"
 *   onClick={() => navigate('/runs?status=RUNNING')}
 * />
 * ```
 */
export function StatCard({
  icon: Icon,
  label,
  value,
  sublabel,
  variant = 'default',
  isLoading = false,
  onClick,
  className,
}: StatCardProps) {
  const isClickable = !!onClick

  return (
    <Card
      className={cn(
        'transition-all',
        isClickable && 'cursor-pointer hover:shadow-md hover:border-primary/50',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={cn('rounded-full p-3', variantStyles[variant])}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">
              {label}
              {sublabel && <span className="block text-xs">{sublabel}</span>}
            </p>
            {isLoading ? (
              <Skeleton className="h-9 w-20 mt-1" data-testid="stat-card-skeleton" />
            ) : (
              <p className="text-3xl font-bold">{value}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
